// Pure helpers over the time-entry timers kept in localStorage — keys, the
// slot shape, owner records and the old-code ("v1") ghost rules.
//
// Every timer writes its whole state into ONE localStorage slot:
//   'lextrack_timer'                legacy in-window timer (old Layout.js); new
//                                   code never creates it, only adopts it
//   'lextrack_timer:<uuid>'         every new timer, from any surface
//   'lextrack_timer:resume:<id>'    one canonical slot per existing entry
// shaped { running, paused, startTime, pausedSeconds, matter_id, matter_title,
// description, ... }.
//
// One-click timers (docs/specs/timers-one-click-spec.md): the SLOT is the
// clock.  A "v2" slot (v:2 plus a sig that still matches its clock fields) is
// written only through lib/timerStore.js, by whichever new-bundle window the
// person used — form, panel, bar.  A slot without v:2, or whose sig no longer
// matches (an old-bundle window rewrote its clock), is "v1": old code holds
// its clock in React refs, so new code never writes it without adopting it
// first (timerStore.adoptV1), and old windows are reached only through the
// pause/resume/focus commands (lib/timerCommands.js).
//
// Everything here is pure (storage + clock are parameters) so the rules are
// unit-tested without a window.

export const LEGACY_SLOT_KEY = 'lextrack_timer'
export const SLOT_PREFIX = 'lextrack_timer:'
export const RESUME_SLOT_PREFIX = 'lextrack_timer:resume:'
// Written by the NEW floating bar (pages/TimerBar.js) as BAR_PROTO_VERSION, so
// the main window can tell the bar is still the pre-update page and say
// "Restart Omnia to finish updating the timer bar".  Registered in uiPrefs.
export const TIMER_BAR_PROTO_KEY = 'lextrack_timer_bar_proto'
export const BAR_PROTO_VERSION = 2
// Ownership records: 'lextrack_timer_owner:<slot key>' = { at, pk }.  An open
// timer window refreshes its record every OWNER_HEARTBEAT_MS; the bar treats a
// record older than OWNER_FRESH_MS as "no window holds this timer" — that is
// how it knows to REOPEN a timer whose window was closed while it ran, rather
// than send a command nobody will hear.  The prefix deliberately does NOT
// start with 'lextrack_timer:' so nothing that scans timer slots (this file,
// Layout's matter.blocked sweep, uiPrefs) mistakes it for a timer.
export const OWNER_PREFIX = 'lextrack_timer_owner:'
export const OWNER_HEARTBEAT_MS = 5000
export const OWNER_FRESH_MS = 15000
// A record that is merely OLD does not mean the window is gone.  Chromium
// throttles timers in a minimized or covered window (once a minute after five
// minutes hidden), so a timer left behind Word stops refreshing its record for
// a minute at a time while it is perfectly alive.  Treating that as "closed"
// opened a SECOND window on the same slot, and saving both would bill the time
// twice.  So: closing a window REMOVES its record (pagehide / unmount), a
// record older than OWNER_FRESH_MS is "quiet" (probably throttled — ask it),
// and only one not refreshed for OWNER_DEAD_MS is treated as gone (a crashed
// window never released it).
export const OWNER_DEAD_MS = 10 * 60 * 1000
// A timer nobody holds and nobody has touched for this long is a leftover,
// not work in progress: before the bar existed, closing a timer window never
// cleared its slot, so every machine carries some.  Hidden (not deleted — new
// code never writes a v1 slot without adopting it).  Running > 24 h is past
// what Save accepts anyway; paused > 7 days is long past a weekend.  These
// rules apply to v1 slots only: a v2 timer is never hidden for age (the
// Timers panel badges it instead — timerStore.listMyTimers).
export const ABANDONED_RUNNING_MS = 24 * 60 * 60 * 1000
export const ABANDONED_PAUSED_MS = 7 * 24 * 60 * 60 * 1000

// The only slot keys the bar will ever hand to PopupTimer's ?slot= (and the
// only ones PopupTimer will adopt).  uuid-ish / resume:<digits> / legacy.
const SLOT_KEY_RE = /^lextrack_timer(?::(?:resume:\d{1,12}|[A-Za-z0-9-]{1,64}))?$/

export function isTimerSlotKey (key) {
  return typeof key === 'string' && SLOT_KEY_RE.test(key)
}

const RESUME_KEY_RE = /^lextrack_timer:resume:(\d{1,12})$/

export const isResumeSlotKey = (key) => typeof key === 'string' && RESUME_KEY_RE.test(key)

// The entry id a resume slot belongs to, as a string of digits, or null.
export function resumeEntryIdOfKey (key) {
  const m = typeof key === 'string' ? RESUME_KEY_RE.exec(key) : null
  return m ? m[1] : null
}

// The ONE slot key for an existing entry (row-click resume and Continue both
// use it, so one entry can never get two slots).  null for a bad id.
export function resumeSlotKey (entryId) {
  const s = String(entryId == null ? '' : entryId)
  return /^\d{1,12}$/.test(s) && Number(s) > 0 ? `${RESUME_SLOT_PREFIX}${s}` : null
}

// RFC 4122 v4 uuid.  Used for slot keys AND for `tid`, which the server takes
// as time_entries.client_ref and refuses (400) unless it is a uuid.
export function newUuid () {
  try {
    const c = typeof window !== 'undefined' ? window.crypto : undefined
    if (c && typeof c.randomUUID === 'function') return c.randomUUID()
    if (c && typeof c.getRandomValues === 'function') {
      const b = c.getRandomValues(new Uint8Array(16))
      return formatUuid(b)
    }
  } catch { /* fall through to Math.random */ }
  const b = new Uint8Array(16)
  for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256)
  return formatUuid(b)
}

function formatUuid (b) {
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

// A fresh 'lextrack_timer:<uuid>' key (moved here from PopupTimer so every
// surface mints the same shape; always matches SLOT_KEY_RE).
export const newSlotKey = () => `${SLOT_PREFIX}${newUuid()}`

// ── v2 slots ─────────────────────────────────────────────────────────────
// sig fingerprints the clock fields at the moment new code last wrote them.
// Old-bundle code spreads the slot when it writes (`{ ...t, paused, ... }`),
// so it keeps v:2 and the old sig while changing the clock — which is exactly
// how an old window rewriting an adopted clock is detected.
export function sigOf (slot) {
  if (!slot || typeof slot !== 'object') return ''
  return `${slot.paused ? 1 : 0}|${slot.startTime}|${slot.pausedSeconds}`
}

export function isV2 (slot) {
  return !!slot && typeof slot === 'object' && slot.v === 2 &&
    typeof slot.sig === 'string' && slot.sig === sigOf(slot)
}

// The floating bar's protocol marker (0 when absent or unreadable).
export function readBarProto (storage) {
  try {
    const n = Number(storage && storage.getItem(TIMER_BAR_PROTO_KEY))
    return Number.isFinite(n) && n > 0 ? n : 0
  } catch {
    return 0
  }
}

// The legacy slot was the old MAIN window's in-page timer.  While it is still
// v1, an old-bundle main window (or, in a browser, an old tab) may hold its
// clock in React refs, so it is never opened in a pop-out: the bar and
// openTimerForm send the main window a 'focus' command instead, and
// adoptableSlotKey refuses it.  Once new code has adopted it (v2 — the desktop
// main window does that silently, a browser after a confirm) it is an ordinary
// timer whose clock lives in the slot, and opens in a pop-out like any other.
export const isLegacySlotKey = (key) => key === LEGACY_SLOT_KEY

const finite = (v) => typeof v === 'number' && Number.isFinite(v)

// A slot the bar can show: an object with running===true and a usable
// startTime — the same test TimerWidget's restore path applies (`t.running &&
// t.startTime`).  Anything else (malformed JSON, a half-written object) is not
// a timer and is ignored rather than shown as 0:00:00.
export function parseSlot (raw) {
  if (typeof raw !== 'string' || !raw) return null
  let t
  try { t = JSON.parse(raw) } catch { return null }
  if (!t || typeof t !== 'object' || Array.isArray(t)) return null
  if (t.running !== true) return null
  const startTime = Number(t.startTime)
  if (!finite(startTime) || startTime <= 0) return null
  return { ...t, startTime }
}

// Every live timer slot in `storage`, as [{ key, slot }].  Never throws.
export function readTimerSlots (storage) {
  const out = []
  if (!storage) return out
  let n = 0
  try { n = storage.length } catch { return out }
  for (let i = 0; i < n; i++) {
    let key
    try { key = storage.key(i) } catch { continue }
    if (key !== LEGACY_SLOT_KEY && !(typeof key === 'string' && key.startsWith(SLOT_PREFIX))) continue
    if (!isTimerSlotKey(key)) continue
    let slot = null
    try { slot = parseSlot(storage.getItem(key)) } catch { slot = null }
    if (slot) out.push({ key, slot })
  }
  return out
}

// Elapsed seconds EXACTLY as TimerWidget shows them:
//   paused  → pausedSeconds              (setElapsed(sp) on restore/pause)
//   ticking → pausedSeconds + floor((now − startTime) / 1000)
//                                        (startInterval's setElapsed)
// Clamped at 0 so a clock change can never draw a negative time.
export function slotElapsedSeconds (slot, now) {
  if (!slot) return 0
  const spRaw = Number(slot.pausedSeconds)
  const sp = finite(spRaw) && spRaw > 0 ? Math.floor(spRaw) : 0
  if (slot.paused) return sp
  const start = Number(slot.startTime)
  if (!finite(start)) return sp
  return Math.max(0, sp + Math.floor((now - start) / 1000))
}

// H:MM:SS — hours unpadded so a short day reads "0:42:07".
export function formatBarElapsed (seconds) {
  const s = finite(seconds) && seconds > 0 ? Math.floor(seconds) : 0
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

export const slotIsTicking = (slot) => !!slot && !slot.paused

// The matter, else the description, else "No matter yet" (one-click timers
// start with no matter; the person picks it later).
export const NO_MATTER_LABEL = 'No matter yet'
export function slotLabel (slot) {
  const pick = (v) => (typeof v === 'string' ? v.trim() : '')
  return pick(slot && slot.matter_title) || pick(slot && slot.description) || NO_MATTER_LABEL
}

// When this timer was last started/resumed (ticking) or paused (paused).
// TimerWidget stamps pausedAt from 1.0.13 on; older slots fall back to
// startTime, which is the last start/resume.
export function lastActivity (slot) {
  if (!slot) return 0
  if (slot.paused) {
    const p = Number(slot.pausedAt)
    if (finite(p)) return p
  }
  const s = Number(slot.startTime)
  return finite(s) ? s : 0
}

// Which timer the bar shows.
//   • The running one — the most recently started/resumed if several.
//   • Else the most recently paused.
//   • A timer the person PICKED from the +N menu (`selected` = { key, at })
//     stays shown until another timer is started or resumed after the pick,
//     so starting a new timer always brings it onto the bar.
// Ties break on the key so the choice is stable across renders.
export function chooseCurrentSlot (entries, selected) {
  const list = Array.isArray(entries) ? entries.filter(e => e && e.slot) : []
  if (list.length === 0) return null
  const byRecent = (a, b) => {
    const d = lastActivity(b.slot) - lastActivity(a.slot)
    if (d !== 0) return d
    return a.key < b.key ? -1 : a.key > b.key ? 1 : 0
  }
  const running = list.filter(e => slotIsTicking(e.slot)).sort(byRecent)
  const auto = running[0] || list.slice().sort(byRecent)[0]
  if (selected && selected.key) {
    const picked = list.find(e => e.key === selected.key)
    if (picked) {
      const pickedAt = Number(selected.at) || 0
      const newer = running.some(e => e.key !== picked.key && lastActivity(e.slot) > pickedAt)
      if (!newer) return picked
    }
  }
  return auto
}

// The signed-in person's id from the JWT payload (no verification — this only
// decides what the bar DISPLAYS; every action still goes through the API with
// the real token).  null when there is no usable token.
export function decodeTokenUserId (token) {
  if (typeof token !== 'string') return null
  const parts = token.split('.')
  if (parts.length < 2 || !parts[1]) return null
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    while (b64.length % 4) b64 += '='
    const json = typeof atob === 'function'
      ? atob(b64)
      : Buffer.from(b64, 'base64').toString('binary')
    const payload = JSON.parse(json)
    const id = payload && payload.id
    return (typeof id === 'number' || typeof id === 'string') && String(id) !== '' ? String(id) : null
  } catch {
    return null
  }
}

// Hide a timer another person started on this machine.  TimerWidget stamps
// `uid` from 1.0.13 on; an older slot without one is shown (it predates the
// stamp and the in-page ⏱ button already showed it to whoever was signed in).
export function visibleSlotsForUser (entries, uid) {
  const list = Array.isArray(entries) ? entries : []
  if (uid == null) return list
  return list.filter(e => e && e.slot && (e.slot.uid == null || String(e.slot.uid) === String(uid)))
}

export const ownerKeyFor = (storageKey) => OWNER_PREFIX + storageKey

// { at, pk, ack, v, by, tab } or null.  `ack` is the id of the last command
// the window HEARD (lib/timerCommands.js) — the sender's proof its click
// arrived.  New-bundle windows also write v:2, by ('popout' | 'inpage') and
// tab (a per-window id); an old-bundle window (desktop 1.0.13/14 pop-out
// still on the pre-update page) writes only { at, pk, ack }, so `v` null on a
// fresh record means "an old window holds this slot" (timerStore classify).
// The old parser read only at/pk/ack, so the extra fields are harmless to it.
export function parseOwner (raw) {
  if (typeof raw !== 'string' || !raw) return null
  try {
    const o = JSON.parse(raw)
    if (!o || typeof o !== 'object') return null
    const at = Number(o.at)
    if (!finite(at)) return null
    return {
      at,
      pk: typeof o.pk === 'string' && o.pk ? o.pk : null,
      ack: typeof o.ack === 'string' && o.ack ? o.ack : null,
      v: o.v === 2 ? 2 : null,
      by: o.by === 'popout' || o.by === 'inpage' ? o.by : null,
      tab: typeof o.tab === 'string' && o.tab ? o.tab : null
    }
  } catch {
    return null
  }
}

export function isOwnerFresh (owner, now) {
  return !!owner && finite(owner.at) && now - owner.at < OWNER_FRESH_MS && owner.at - now < OWNER_FRESH_MS
}

// 'none'  — no record: no window holds it (closing a window removes it).
// 'live'  — refreshed within OWNER_FRESH_MS.
// 'quiet' — older, but a throttled background window can be this quiet.
// 'dead'  — not refreshed for OWNER_DEAD_MS: a window that crashed.
export function ownerState (owner, now) {
  if (!owner || !finite(owner.at)) return 'none'
  const age = Math.abs(now - owner.at)
  if (age < OWNER_FRESH_MS) return 'live'
  if (age < OWNER_DEAD_MS) return 'quiet'
  return 'dead'
}

// A leftover the bar should not offer.  A slot a window holds is never one.
// Of the slots no window holds:
//   • ran === false — opened on an existing entry (or by "Continue this
//     entry") and never started: once its window is gone there is nothing to
//     come back to.
//   • no `ran` stamp at all — written before this release, when closing a
//     timer window stranded its slot for good and nobody was ever offered it
//     again.  Every machine carries some; surfacing them now would greet
//     people with weeks-old timers (and a stale resume slot would save its
//     old hours over the entry).  They stay invisible, as they always were.
//     TimerWidget stamps `ran` on every slot it writes from this release on.
//     The main window's legacy slot is exempt: Layout writes it unstamped and
//     restores it itself.
//   • otherwise, past ABANDONED_RUNNING_MS / ABANDONED_PAUSED_MS.
export function isAbandonedSlot (slot, owner, now, key = null) {
  if (!slot) return true
  const st = ownerState(owner, now)
  if (st === 'live' || st === 'quiet') return false
  if (slot.ran === false) return true
  if (slot.ran !== true && !isLegacySlotKey(key)) return true
  const age = now - lastActivity(slot)
  return age > (slot.paused ? ABANDONED_PAUSED_MS : ABANDONED_RUNNING_MS)
}

export function readOwner (storage, storageKey) {
  try { return parseOwner(storage.getItem(ownerKeyFor(storageKey))) } catch { return null }
}

// The slots the bar offers: not abandoned.
export function liveSlotsForBar (entries, storage, now) {
  const list = Array.isArray(entries) ? entries : []
  return list.filter(e => e && e.slot && !isAbandonedSlot(e.slot, readOwner(storage, e.key), now, e.key))
}

// The popupKey the bar uses when it has to (re)open a timer's window.  A
// resume slot uses the SAME key every other surface uses for that entry
// (Layout / useTimerEventBridge: 'timer-resume-<id>'), so if that window is in
// fact open the desktop app focuses it instead of opening a second one.
export function popupKeyForSlot (storageKey) {
  const m = /^lextrack_timer:resume:(\d+)$/.exec(storageKey || '')
  if (m) return `timer-resume-${m[1]}`
  if (storageKey === LEGACY_SLOT_KEY) return 'timer-slot-legacy'
  return `timer-slot-${String(storageKey || '').slice(SLOT_PREFIX.length)}`
}

// Reopen a timer window ON its saved slot: PopupTimer adopts ?slot= and
// TimerWidget's normal restore path brings the clock back exactly as it was
// (ticking keeps ticking from its original start; paused stays paused).  The
// resume route is deliberately NOT used for resume slots — it re-seeds the
// clock from the server's hours and would drop the time run since.
export function reopenRouteForSlot (storageKey, popupKey) {
  const q = new URLSearchParams()
  q.set('slot', storageKey)
  if (popupKey) q.set('pk', popupKey)
  return `/popup/timer/new?${q.toString()}`
}

// popupKey values accepted from a URL (?pk=) — what our own spawners mint.
const POPUP_KEY_RE = /^[A-Za-z0-9:._-]{1,160}$/
export function sanitizePopupKey (pk) {
  return typeof pk === 'string' && POPUP_KEY_RE.test(pk) ? pk : null
}

// `route` with ?pk=<popupKey> added, so the timer window it opens can record
// the key it was opened under (PopupTimer → TimerWidget heartbeat) and the
// bar can focus that exact window later.  Every opener of a timer window
// should pass its route through this.  A key that would not survive
// sanitizePopupKey is left off rather than sent.
export function tagRouteWithPopupKey (route, popupKey) {
  const pk = sanitizePopupKey(popupKey)
  if (typeof route !== 'string' || !pk) return route
  const hashAt = route.indexOf('#')
  const base = hashAt === -1 ? route : route.slice(0, hashAt)
  const hash = hashAt === -1 ? '' : route.slice(hashAt)
  return `${base}${base.includes('?') ? '&' : '?'}pk=${encodeURIComponent(pk)}${hash}`
}

// The slot key PopupTimer should adopt from ?slot=, or null.  Only a valid
// timer key that still holds a live timer is adopted; anything else (a timer
// saved elsewhere since, a hand-typed URL) opens a fresh timer as before.
// The legacy slot only once it is v2 (adopted by new code: its clock lives in
// the slot, so a pop-out form on it is just another view) — while it is v1 an
// old main window may hold its clock in refs, and two clocks on one slot
// could both be saved.  Never a timer another person started on this
// machine (`uid` = the signed-in user's id, when known).
export function adoptableSlotKey (param, storage, uid = null) {
  if (!isTimerSlotKey(param)) return null
  try {
    const slot = parseSlot(storage.getItem(param))
    if (!slot) return null
    if (isLegacySlotKey(param) && !isV2(slot)) return null
    if (uid != null && slot.uid != null && String(slot.uid) !== String(uid)) return null
    return param
  } catch {
    return null
  }
}
